import { useMemo, useState } from "react";
import { X, Upload } from "lucide-react";
import { useParams } from "react-router-dom";
import { useConfigStore } from "../../../../store/configStore";
import { useEditorStore } from "../../../../store/editorStore";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import {
  spineStaticOriginalUrl,
  spineStaticThumbUrl,
} from "commons/cdn";
import { StaticSkuUploadDialog } from "../list-components/StaticSkuUploadDialog";

/** 静态层模版专用：上传原图/缩略图资源 ID，与 Spine Skin SKU 详情（SkuDetailPanel）无关 */
export function StaticSkuDetailPanel() {
  const { tid } = useParams<{ tid: string }>();
  const templateConfig = useConfigStore((s) => s.templateConfig);
  const {
    editingSkuId,
    setEditingSku,
    updateStaticSkuField,
    removeStaticSkuFromSelection,
  } = useEditorStore();

  const [imageUploadOpen, setImageUploadOpen] = useState(false);

  const sku = useMemo(() => {
    if (!editingSkuId || !templateConfig?.skus) return null;
    return templateConfig.skus[editingSkuId] || null;
  }, [editingSkuId, templateConfig?.skus]);

  const selectionIdForSku = useMemo(() => {
    if (!editingSkuId || !templateConfig?.selection) return null;
    for (const sel of templateConfig.selection) {
      const ids = sel.skuIds || [];
      if (ids.includes(editingSkuId)) return sel.id;
    }
    return null;
  }, [editingSkuId, templateConfig?.selection]);

  if (!sku || sku.category !== "static") {
    return null;
  }

  const origUrl = sku.originalAssetId
    ? spineStaticOriginalUrl(sku.originalAssetId)
    : "";
  const thumbUrl = sku.thumbAssetId
    ? spineStaticThumbUrl(sku.thumbAssetId)
    : "";

  const hasImage = !!(sku.originalAssetId || sku.thumbAssetId);

  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-2 flex-shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">静态 SKU</CardTitle>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            onClick={() => setEditingSku(null, null)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs font-mono text-muted-foreground">ID: {sku.id}</p>
      </CardHeader>
      <CardContent className="flex-1 overflow-auto space-y-4">
        <StaticSkuUploadDialog
          mode="replace"
          replaceSkuId={editingSkuId}
          open={imageUploadOpen}
          onOpenChange={setImageUploadOpen}
        />
        <div>
          <Button
            variant="outline"
            size="sm"
            className="gap-1"
            disabled={!tid}
            onClick={() => setImageUploadOpen(true)}
          >
            <Upload className="h-4 w-4" />
            {hasImage ? "更换图片" : "选择图片上传"}
          </Button>
          <p className="text-xs text-muted-foreground mt-2">
            与列表添加一致：在弹窗中选图 → 先上传生成资源 ID → 再写入本条 SKU。上传完成后可配置下方价格与显示选项。
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">原图</Label>
            <div className="mt-1 aspect-square rounded border bg-muted/30 flex items-center justify-center overflow-hidden">
              {origUrl ? (
                <img src={origUrl} alt="" className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-xs text-muted-foreground">暂无</span>
              )}
            </div>
            {sku.originalAssetId && (
              <p className="text-[10px] font-mono break-all mt-1 text-muted-foreground">
                o:{sku.originalAssetId}
              </p>
            )}
          </div>
          <div>
            <Label className="text-xs">缩略图</Label>
            <div className="mt-1 aspect-square rounded border bg-muted/30 flex items-center justify-center overflow-hidden">
              {thumbUrl ? (
                <img src={thumbUrl} alt="" className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-xs text-muted-foreground">暂无</span>
              )}
            </div>
            {sku.thumbAssetId && (
              <p className="text-[10px] font-mono break-all mt-1 text-muted-foreground">
                t:{sku.thumbAssetId}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label className={cn(!hasImage && "text-muted-foreground")}>
            价格（pro）
          </Label>
          <Input
            type="number"
            min={0}
            value={sku.pro ?? 0}
            disabled={!hasImage}
            title={!hasImage ? "请先上传图片" : undefined}
            onChange={(e) => {
              const n = parseInt(e.target.value, 10);
              if (!Number.isNaN(n) && n >= 0) {
                updateStaticSkuField(sku.id, { pro: n });
              }
            }}
          />
          {!hasImage && (
            <p className="text-xs text-muted-foreground">上传图片后可编辑</p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="static-sku-hidden"
            checked={!!sku.hidden}
            disabled={!hasImage}
            onCheckedChange={(c) =>
              updateStaticSkuField(sku.id, { hidden: c === true })
            }
          />
          <Label
            htmlFor="static-sku-hidden"
            className={cn(
              "text-sm font-normal",
              !hasImage && "text-muted-foreground",
            )}
          >
            App 中隐藏
          </Label>
        </div>

        {selectionIdForSku && (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => {
              removeStaticSkuFromSelection(selectionIdForSku, sku.id);
              setEditingSku(null, null);
            }}
          >
            从分组移除并删除 SKU
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
